// # Filename: src/components/poker/hooks/usePokerTableView.js
//
// Breakpoint-agnostic poker table view-model: derived player/seat data, turn timer,
// deal/payout animation state, and action handlers. No JSX, no positioning math --
// each tier's table component supplies its own geometry (via `getChipFallbackPoint`)
// and owns its own DOM/layout, but they all read the same game state and animation
// timing from here so that logic never has to be duplicated per breakpoint.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  chipValueForSeat,
  getBlindSeats,
  hasFaceUpCards,
  isEliminatedPlayer,
  optimisticActionLabel,
  playersFromGame,
} from "../shared/pokerGameUtils";

const CHIP_TRANSFER_STACK_DURATION_MS = 1950;
const CHIP_TRANSFER_POT_DURATION_MS = 980;

export function usePokerTableView({ game, handResultAnimation, onAction, getChipFallbackPoint }) {
  const [raiseTo, setRaiseTo] = useState(0);
  const [raisePanelOpen, setRaisePanelOpen] = useState(false);
  const [clientNow, setClientNow] = useState(Date.now());
  const [visibleHoleCards, setVisibleHoleCards] = useState(Number.POSITIVE_INFINITY);
  const [visibleCommunityCards, setVisibleCommunityCards] = useState(Number.POSITIVE_INFINITY);
  const [pendingSeatActions, setPendingSeatActions] = useState({});
  const [chipCountOverrides, setChipCountOverrides] = useState({});
  const [potCountOverride, setPotCountOverride] = useState(null);
  const [activeChipTransfer, setActiveChipTransfer] = useState(null);
  const [measuredChipTargets, setMeasuredChipTargets] = useState({});

  const dealStateRef = useRef({ handNumber: null, communityCount: 0 });
  const animatedChipTransferKeyRef = useRef(null);
  const tableRef = useRef(null);
  const stackAmountRefs = useRef({});

  const mySeat = game?.my_seat;
  const handNumber = game?.hand_number || 1;
  const communityCount = game?.community_cards?.length || 0;

  const tablePlayers = useMemo(() => playersFromGame(game), [game]);
  const totalHoleCards = useMemo(
    () => tablePlayers.reduce((total, player) => total + (player.cards?.length || 0), 0),
    [tablePlayers]
  );
  const holeRevealBySeat = useMemo(() => {
    const counts = {};
    const ordered = [...tablePlayers].sort((a, b) => Number(a.seat) - Number(b.seat));
    let revealIndex = 0;
    for (let cardIndex = 0; cardIndex < 2; cardIndex += 1) {
      for (const player of ordered) {
        if ((player.cards || [])[cardIndex]) {
          revealIndex += 1;
          if (Number(player.seat) === Number(mySeat) || visibleHoleCards >= revealIndex) {
            counts[player.seat] = (counts[player.seat] || 0) + 1;
          }
        }
      }
    }
    return counts;
  }, [mySeat, tablePlayers, visibleHoleCards]);
  const displayedTablePlayers = useMemo(
    () => tablePlayers.map((player) => ({
      ...player,
      eliminated: isEliminatedPlayer(player),
      cards: (player.cards || []).slice(0, holeRevealBySeat[player.seat] || 0),
    })),
    [holeRevealBySeat, tablePlayers]
  );
  const activeTablePlayers = useMemo(
    () => tablePlayers.filter((player) => !isEliminatedPlayer(player)),
    [tablePlayers]
  );
  const activeDisplayedTablePlayers = useMemo(
    () => displayedTablePlayers.filter((player) => !player.eliminated),
    [displayedTablePlayers]
  );
  const displayedCommunityCards = useMemo(
    () => (game?.community_cards || []).slice(0, visibleCommunityCards),
    [game?.community_cards, visibleCommunityCards]
  );
  const me = displayedTablePlayers.find((player) => Number(player.seat) === Number(mySeat)) || displayedTablePlayers[0];
  const rawMe = tablePlayers.find((player) => Number(player.seat) === Number(mySeat)) || tablePlayers[0];
  const heroEliminated = isEliminatedPlayer(rawMe);
  const heroFeltPlayer = heroEliminated
    ? { ...me, cards: [], eliminated: true }
    : hasFaceUpCards(rawMe?.cards)
    ? { ...me, cards: rawMe.cards, folded: rawMe.folded }
    : me;
  const opponents = activeDisplayedTablePlayers.filter((player) => Number(player.seat) !== Number(mySeat));

  const registerStackAmountRef = useCallback((seat) => (node) => {
    const key = String(Number(seat));
    if (node) {
      stackAmountRefs.current[key] = node;
    } else {
      delete stackAmountRefs.current[key];
    }
  }, []);

  useLayoutEffect(() => {
    const winners = activeChipTransfer?.result?.winners || [];
    const tableNode = tableRef.current;
    if (!winners.length || !tableNode) {
      setMeasuredChipTargets({});
      return;
    }

    const tableRect = tableNode.getBoundingClientRect();
    if (!tableRect.width || !tableRect.height) return;

    const nextTargets = {};
    winners.forEach((winner) => {
      const seat = Number(winner.seat);
      const node = stackAmountRefs.current[String(seat)];
      if (!seat || !node) return;
      const rect = node.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      nextTargets[seat] = {
        xPct: ((rect.left + rect.width / 2 - tableRect.left) / tableRect.width) * 100,
        yPct: ((rect.top + rect.height / 2 - tableRect.top) / tableRect.height) * 100,
      };
    });
    setMeasuredChipTargets(nextTargets);
  }, [activeChipTransfer]);

  const chipTransferStreams = useMemo(() => {
    const result = activeChipTransfer?.result;
    if (!result?.winners?.length) return [];
    return result.winners
      .map((winner) => {
        const seat = Number(winner.seat);
        if (!seat) return null;
        const measuredTarget = measuredChipTargets[seat];
        const isHero = seat === Number(mySeat);
        const opponentIndex = isHero ? -1 : opponents.findIndex((player) => Number(player.seat) === seat);
        if (!isHero && opponentIndex < 0) return null;

        const point = measuredTarget || getChipFallbackPoint({
          seat,
          isHero,
          opponentIndex,
          opponentCount: opponents.length,
        });
        return {
          key: `${activeChipTransfer.key}-${seat}`,
          seat,
          amount: winner.amount,
          end: point,
        };
      })
      .filter(Boolean);
  }, [activeChipTransfer, getChipFallbackPoint, measuredChipTargets, mySeat, opponents]);

  const payoutPulseSeats = useMemo(
    () => new Set((activeChipTransfer?.result?.winners || []).map((winner) => Number(winner.seat)).filter(Boolean)),
    [activeChipTransfer]
  );

  const isMyTurn = game?.current_turn === mySeat && !game?.is_completed;
  const can = (action) => game?.legal_actions?.includes(action);
  const minRaiseTo = game?.min_raise_to;
  const maxRaiseTo = game?.max_raise_to;

  const serverOffset = useMemo(() => {
    if (!game?.server_now) return 0;
    const parsed = Date.parse(game.server_now);
    return Number.isFinite(parsed) ? parsed - Date.now() : 0;
  }, [game?.server_now]);
  const timerRemaining = useMemo(() => {
    if (!game?.turn_deadline_at || game?.is_completed) return null;
    const deadline = Date.parse(game.turn_deadline_at);
    if (!Number.isFinite(deadline)) return null;
    return Math.max(0, Math.ceil((deadline - (clientNow + serverOffset)) / 1000));
  }, [clientNow, game?.is_completed, game?.turn_deadline_at, serverOffset]);
  const timerPercent = timerRemaining == null
    ? 0
    : Math.max(0, Math.min(100, (timerRemaining / Math.max(1, game?.turn_timer_seconds || 1)) * 100));
  const timerUrgent = timerRemaining != null && timerRemaining <= 8;

  const { smallBlindSeat, bigBlindSeat } = useMemo(
    () => getBlindSeats(game, activeTablePlayers),
    [activeTablePlayers, game]
  );
  const roleForSeat = useCallback((seat) => {
    const seatNumber = Number(seat);
    if (seatNumber === Number(game?.dealer)) return "D";
    if (seatNumber === Number(smallBlindSeat)) return "SB";
    if (seatNumber === Number(bigBlindSeat)) return "BB";
    return "";
  }, [bigBlindSeat, game?.dealer, smallBlindSeat]);

  const pendingLabelForSeat = useCallback((seat) => pendingSeatActions[String(seat)] || "", [pendingSeatActions]);
  const clearPendingSeatAction = useCallback((seat) => {
    if (seat == null) return;
    setPendingSeatActions((current) => {
      const key = String(seat);
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const handleAction = useCallback((action, amount = null) => {
    const seat = mySeat;
    const label = optimisticActionLabel(action, amount, game);
    if (seat != null && label) {
      setPendingSeatActions((current) => ({
        ...current,
        [String(seat)]: label,
      }));
    }

    try {
      const result = onAction(action, amount);
      if (result && typeof result.catch === "function") {
        result.catch(() => clearPendingSeatAction(seat));
      }
      return result;
    } catch (error) {
      clearPendingSeatAction(seat);
      throw error;
    }
  }, [clearPendingSeatAction, game, mySeat, onAction]);

  const canRaiseOrAllIn = can("raise") || can("all_in");
  const raisePresets = useMemo(() => {
    if (!minRaiseTo || !maxRaiseTo) return [];
    const halfPot = Math.ceil(((game?.pot || 0) / 2 + (game?.current_bet || 0)) / 10) * 10;
    const pot = Math.ceil(((game?.pot || 0) + (game?.current_bet || 0)) / 10) * 10;
    return Array.from(new Set([minRaiseTo, halfPot, pot, maxRaiseTo]))
      .filter((amount) => amount >= minRaiseTo && amount <= maxRaiseTo)
      .slice(0, 4);
  }, [game?.current_bet, game?.pot, maxRaiseTo, minRaiseTo]);

  useEffect(() => {
    if (minRaiseTo) setRaiseTo(minRaiseTo);
  }, [minRaiseTo, game?.hand_number, game?.phase, game?.current_bet]);

  useEffect(() => {
    if (!game?.legal_actions?.includes("raise") && !game?.legal_actions?.includes("all_in")) {
      setRaisePanelOpen(false);
    }
  }, [game?.legal_actions]);

  useEffect(() => {
    setPendingSeatActions({});
  }, [handNumber]);

  useEffect(() => {
    setPendingSeatActions((current) => {
      let changed = false;
      const next = { ...current };
      Object.keys(next).forEach((seat) => {
        if (Number(seat) !== Number(game?.current_turn)) {
          delete next[seat];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [game?.current_turn]);

  useEffect(() => {
    const result = handResultAnimation?.result;
    if (!handResultAnimation?.key || !result?.winners?.length) return undefined;
    if (animatedChipTransferKeyRef.current === handResultAnimation.key) return undefined;
    animatedChipTransferKeyRef.current = handResultAnimation.key;

    const winners = result.winners
      .map((winner) => ({
        seat: Number(winner.seat),
        amount: Number(winner.amount || 0),
      }))
      .filter((winner) => winner.seat && winner.amount > 0);
    if (!winners.length) return undefined;

    const previousGame = handResultAnimation.previousGame;
    const finalStacks = {};
    const startStacks = {};
    winners.forEach((winner) => {
      finalStacks[winner.seat] = chipValueForSeat(game, winner.seat);
      startStacks[winner.seat] = previousGame
        ? chipValueForSeat(previousGame, winner.seat)
        : finalStacks[winner.seat] - winner.amount;
    });

    const startPot = Math.max(
      Number(previousGame?.pot || 0),
      winners.reduce((total, winner) => total + winner.amount, 0)
    );
    const startedAt = performance.now();
    let frame = null;
    let settleTimer = null;

    setActiveChipTransfer(handResultAnimation);
    setPotCountOverride(startPot);
    setChipCountOverrides(startStacks);

    const tick = (now) => {
      const elapsed = now - startedAt;
      const stackProgress = Math.min(1, elapsed / CHIP_TRANSFER_STACK_DURATION_MS);
      const potProgress = Math.min(1, elapsed / CHIP_TRANSFER_POT_DURATION_MS);
      const easedStack = 1 - Math.pow(1 - stackProgress, 3);
      const easedPot = 1 - Math.pow(1 - potProgress, 3);
      const nextStacks = {};
      winners.forEach((winner) => {
        nextStacks[winner.seat] = Math.round(
          startStacks[winner.seat] + (finalStacks[winner.seat] - startStacks[winner.seat]) * easedStack
        );
      });
      setChipCountOverrides(nextStacks);
      setPotCountOverride(Math.max(0, Math.round(startPot * (1 - easedPot))));

      if (stackProgress < 1) {
        frame = window.requestAnimationFrame(tick);
        return;
      }

      setChipCountOverrides({});
      setPotCountOverride(null);
      settleTimer = window.setTimeout(() => setActiveChipTransfer(null), 180);
    };

    frame = window.requestAnimationFrame(tick);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (settleTimer) window.clearTimeout(settleTimer);
    };
  }, [game, handResultAnimation]);

  useEffect(() => {
    const interval = window.setInterval(() => setClientNow(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!game?.id) return undefined;
    const previous = dealStateRef.current;

    if (game.is_completed) {
      setVisibleHoleCards(Number.POSITIVE_INFINITY);
      setVisibleCommunityCards(Number.POSITIVE_INFINITY);
      dealStateRef.current = {
        handNumber,
        communityCount: 0,
      };
      return undefined;
    }

    if (previous.handNumber === handNumber) return undefined;

    dealStateRef.current = {
      handNumber,
      communityCount: 0,
    };
    setVisibleCommunityCards(0);

    if (!totalHoleCards) {
      setVisibleHoleCards(0);
      return undefined;
    }

    setVisibleHoleCards(0);
    const timers = Array.from({ length: totalHoleCards }, (_, idx) => (
      window.setTimeout(() => setVisibleHoleCards(idx + 1), 140 + idx * 115)
    ));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [game?.id, game?.is_completed, handNumber, totalHoleCards]);

  useEffect(() => {
    if (!game?.id || game.is_completed) return undefined;
    const nextCount = communityCount;
    const previous = dealStateRef.current;

    if (previous.handNumber !== handNumber) return undefined;
    if (nextCount <= previous.communityCount) return undefined;

    const startCount = previous.communityCount;
    dealStateRef.current = { handNumber, communityCount: nextCount };
    setVisibleCommunityCards(startCount);

    const timers = Array.from({ length: nextCount - startCount }, (_, idx) => (
      window.setTimeout(() => setVisibleCommunityCards(startCount + idx + 1), 160 + idx * 150)
    ));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [communityCount, game?.id, game?.is_completed, handNumber]);

  return {
    tableRef,
    registerStackAmountRef,

    mySeat,
    handNumber,
    tablePlayers,
    displayedTablePlayers,
    activeTablePlayers,
    activeDisplayedTablePlayers,
    displayedCommunityCards,
    me,
    heroEliminated,
    heroFeltPlayer,
    opponents,

    chipCountOverrides,
    potCountOverride,
    chipTransferStreams,
    payoutPulseSeats,

    isMyTurn,
    can,
    minRaiseTo,
    maxRaiseTo,
    timerRemaining,
    timerPercent,
    timerUrgent,
    smallBlindSeat,
    bigBlindSeat,
    roleForSeat,
    pendingLabelForSeat,

    raiseTo,
    setRaiseTo,
    raisePanelOpen,
    setRaisePanelOpen,
    canRaiseOrAllIn,
    raisePresets,
    handleAction,
  };
}
